/** @format */

import sidebarData from "../components/sidebar/sidebar_data";

/**
 * Decodes permission string from cookie format (e.g., "1018%2C6%2C10008" -> ["1018", "6", "10008"])
 * @param {string} permissionString - URL encoded permission string
 * @returns {string[]} - Array of permission IDs
 */
export const decodePermissions = (permissionString) => {
  try {
    if (!permissionString || typeof permissionString !== "string") {
      return [];
    }

    // Decode URL encoded string and split by comma
    const decoded = decodeURIComponent(permissionString);
    return decoded
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);
  } catch (error) {
    console.error("Error decoding permissions:", error);
    return [];
  }
};

/**
 * Gets user permissions from cookies
 * @returns {string[]} - Array of permission IDs
 */
export const getUserPermissions = () => {
  try {
    const permissionCookie = document.cookie
      .split("; ")
      .find((row) => row.startsWith("permissions="))
      ?.split("=")[1];

    if (permissionCookie) {
      return decodePermissions(permissionCookie);
    }

    // Fallback to localStorage
    const stored = localStorage.getItem("permissions");
    if (stored) {
      return decodePermissions(stored);
    }

    return [];
  } catch (error) {
    console.error("Error getting user permissions:", error);
    return [];
  }
};

/**
 * Checks if user has permission for a specific feature
 * @param {string[]} userPermissions - User's permission IDs
 * @param {string[]} requiredPermissions - Required permission IDs for the feature
 * @returns {boolean} - Whether user has access
 */
export const hasPermissionForFeature = (
  userPermissions,
  requiredPermissions
) => {
  if (!Array.isArray(userPermissions) || !Array.isArray(requiredPermissions)) {
    return false;
  }

  return requiredPermissions.some((permission) =>
    userPermissions.includes(permission.toString())
  );
};

/**
 * Finds the first accessible route based on user permissions
 * @param {string[]} userPermissions - User's permission IDs (optional, will get from cookies if not provided)
 * @returns {string} - URL of the first accessible route or default route
 */
export const getFirstAccessibleRoute = (userPermissions = null) => {
  try {
    // Get permissions if not provided
    const permissions = userPermissions || getUserPermissions();

    if (!Array.isArray(permissions) || permissions.length === 0) {
      console.warn("No permissions found, redirecting to default route");
      return "/emp-dashboard"; // Default fallback
    }

    console.log("User permissions:", permissions);

    // Sort sidebar data by _id to ensure consistent ordering
    const sortedSidebarData = [...sidebarData].sort((a, b) => {
      const idA = parseInt(a._id) || 0;
      const idB = parseInt(b._id) || 0;
      return idA - idB;
    });

    // Find the first module the user has access to
    for (const module of sortedSidebarData) {
      const hasMainPermission = hasPermissionForFeature(
        permissions,
        module.requiredPermissionsformainfeatures || []
      );

      if (hasMainPermission) {
        console.log(`Found accessible module: ${module.name} (${module._id})`);

        // If module has submodules, find the first accessible submodule
        if (
          module.subModules &&
          Array.isArray(module.subModules) &&
          module.subModules.length > 0
        ) {
          for (const subModule of module.subModules) {
            const hasSubPermission = hasPermissionForFeature(
              permissions,
              subModule.requiredPermissionsforsubfeatures || []
            );

            if (hasSubPermission) {
              // If main module has URL, concatenate; otherwise use submodule URL directly
              const fullUrl = module.url
                ? `${module.url}${subModule.url}`
                : subModule.url;
              console.log(
                `Redirecting to submodule: ${subModule.name} -> ${fullUrl}`
              );
              return fullUrl;
            }
          }

          // If no accessible submodules but has main permission, go to main module
          if (module.url) {
            console.log(
              `No accessible submodules, redirecting to main module: ${module.url}`
            );
            return module.url;
          }
        } else {
          // No submodules, redirect to main module
          if (module.url) {
            console.log(`Redirecting to main module: ${module.url}`);
            return module.url;
          }
        }
      }
    }

    // If no accessible routes found, return default
    console.warn("No accessible routes found, using default");
    return "/emp-dashboard";
  } catch (error) {
    console.error("Error determining accessible route:", error);
    return "/emp-dashboard"; // Safe fallback
  }
};

/**
 * Redirects user to the first accessible route based on their permissions
 * @param {Function} navigate - React Router navigate function
 * @param {string[]} userPermissions - Optional user permissions array
 * @param {number} delay - Optional delay in milliseconds before redirect
 */
export const redirectToFirstAccessibleRoute = (
  navigate,
  userPermissions = null,
  delay = 0
) => {
  try {
    const targetRoute = getFirstAccessibleRoute(userPermissions);

    if (delay > 0) {
      setTimeout(() => {
        navigate(targetRoute);
      }, delay);
    } else {
      navigate(targetRoute);
    }

    console.log(
      `Redirecting to: ${targetRoute}${delay > 0 ? ` (after ${delay}ms)` : ""}`
    );
  } catch (error) {
    console.error("Error during redirect:", error);
    // Fallback redirect
    if (delay > 0) {
      setTimeout(() => {
        navigate("/emp-dashboard");
      }, delay);
    } else {
      navigate("/emp-dashboard");
    }
  }
};

export default {
  decodePermissions,
  getUserPermissions,
  hasPermissionForFeature,
  getFirstAccessibleRoute,
  redirectToFirstAccessibleRoute,
};
